/**
 * Which per-code fields do not apply to a code, and why (BV-2742).
 *
 * The Call Script's multi-code grid greys out a cell that cannot apply to its code — a
 * copay for a drug that is not covered, PA details for a drug that needs no PA — with
 * the reason, instead of offering an input nobody should fill. Neither the disposition
 * engine nor the script says this, so it lives here as a per-workflow table.
 *
 * Deliberately conservative:
 *   - A rule only fires once its gating answer is KNOWN. An unanswered or unrecognised
 *     gate leaves the field applicable, so an unexpected enum value can never hide a
 *     field.
 *   - The caller only asks about fields the engine does not require. The engine is the
 *     source of truth for what must be captured; if it requires a field a rule would grey
 *     out, the rule is wrong and the field stays editable.
 *
 * Pure (no React, no I/O).
 */
import { readRelativePath } from './fieldStatus';

/** A yes/no gating answer, read loosely from whatever shape the schema stores. */
type Answer = 'yes' | 'no' | 'unknown';

const YES_TOKENS = new Set(['yes', 'true', 'covered', 'required']);
const NO_TOKENS = new Set(['no', 'false', 'noncovered']);

/**
 * Read a gating value as yes / no / unknown.
 *
 * Output values arrive as booleans, plain strings (`covered`, `not_covered`) or proto
 * enum names (`COVERAGE_STATUS_NOT_COVERED`, `YES_NO_YES`), so the LAST token decides,
 * with a `not_` in front of it flipping a yes-word to no. Anything else — including a
 * qualified answer such as `covered_with_restrictions` — is `unknown`, which keeps the
 * dependent fields applicable.
 */
export function answerOf(value: unknown): Answer {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  if (typeof value !== 'string') return 'unknown';
  const tokens = value
    .trim()
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean);
  const last = tokens[tokens.length - 1];
  if (!last) return 'unknown';
  if (NO_TOKENS.has(last)) return 'no';
  if (YES_TOKENS.has(last)) return tokens[tokens.length - 2] === 'not' ? 'no' : 'yes';
  return 'unknown';
}

/** One gate: `fields` only apply while the answer at `gate` is `expect`. */
interface ApplicabilityRule {
  /** Relative paths inside one entity element. A trailing `.*` matches a whole object. */
  fields: readonly string[];
  /** Relative paths that a `.*` pattern above must NOT match (the gate itself, usually). */
  except?: readonly string[];
  /** Relative path of the answer that decides. */
  gate: string;
  expect: 'yes' | 'no';
  /** Shown in the greyed cell after "n/a · ". Lower-case, reads after a dot. */
  reason: string;
}

const COVERED_ONLY = [
  'coverage.copay',
  'coverage.coinsurance',
  'coverage.copay_applies',
  'coverage.coinsurance_applies',
  'coverage.deductible_applies',
  'coverage.oopmax_applies',
  'coverage.covered_hundred_percent',
];

const EXCEPTION_DETAILS = [
  'coverage.coverage_exception_process',
  'coverage.coverage_exception_phone',
  'coverage.coverage_exception_fax',
  'coverage.coverage_exception_website',
  'coverage.coverage_exception_turnaround_time',
];

const PA_ON_FILE_DETAILS = [
  'prior_auth_status.prior_authorization_on_file',
  'prior_auth_status.prior_authorization_on_file_still_applicable',
  'prior_auth_status.prior_authorization_effective_date',
  'prior_auth_status.prior_authorization_end_date',
  'prior_auth_status.prior_authorization_reference_number',
  'prior_auth_status.prior_authorization_quantity_limits',
];

const PRE_DETERMINATION_ON_FILE_DETAILS = [
  'pre_determination_status.pre_determination_on_file',
  'pre_determination_status.pre_determination_effective_date',
  'pre_determination_status.pre_determination_end_date',
  'pre_determination_status.pre_determination_reference_number',
  'pre_determination_status.pre_determination_quantity_limits',
  'pre_determination_status.pre_determination_quantity_limits_used',
];

/**
 * The MedBV drug / procedure element (nomos medbv output). Both entities share these
 * relative paths; a rule naming a path an entity lacks simply never matches.
 * Order matters only for the reason shown: the first failing gate wins, so broader
 * gates (coverage) come before narrower ones (exception process exists).
 */
const MEDBV_ENTITY_RULES: readonly ApplicabilityRule[] = [
  {
    fields: COVERED_ONLY,
    gate: 'coverage.coverage_status',
    expect: 'yes',
    reason: 'only if covered',
  },
  {
    fields: [
      'coverage.reason_for_noncoverage',
      'coverage.coverage_exception_process_exists',
      'coverage.coverage_under_pbm',
      ...EXCEPTION_DETAILS,
    ],
    gate: 'coverage.coverage_status',
    expect: 'no',
    reason: 'only if not covered',
  },
  {
    fields: EXCEPTION_DETAILS,
    gate: 'coverage.coverage_exception_process_exists',
    expect: 'yes',
    reason: 'only if an exception process exists',
  },
  {
    fields: ['pbm.*'],
    gate: 'coverage.coverage_under_pbm',
    expect: 'yes',
    reason: 'only if covered under the PBM',
  },
  {
    fields: ['prior_auth_status.*', 'prior_auth_process.*'],
    except: ['prior_auth_status.prior_authorization_required'],
    gate: 'prior_auth_status.prior_authorization_required',
    expect: 'yes',
    reason: 'only if PA required',
  },
  {
    fields: PA_ON_FILE_DETAILS,
    gate: 'prior_auth_status.prior_authorization_on_file_exists',
    expect: 'yes',
    reason: 'only if a PA is on file',
  },
  {
    fields: PRE_DETERMINATION_ON_FILE_DETAILS,
    gate: 'pre_determination_status.pre_determination_on_file_exists',
    expect: 'yes',
    reason: 'only if a pre-determination is on file',
  },
  {
    fields: ['appeal.*'],
    except: ['appeal.prior_authorization_appeal_available'],
    gate: 'appeal.prior_authorization_appeal_available',
    expect: 'yes',
    reason: 'only if an appeal is available',
  },
  {
    fields: ['step_therapy.step_therapy_treatments_required'],
    gate: 'step_therapy.step_therapy_required',
    expect: 'yes',
    reason: 'only if step therapy required',
  },
];

const MEDBV_ENTITIES = { drug: MEDBV_ENTITY_RULES, procedure: MEDBV_ENTITY_RULES };

/**
 * workflow_type → entity key → rules. A workflow or entity with no entry has no n/a
 * cells. medbhobv is absent on purpose: its script stays one card per code until the
 * disposition engine understands its `medbv_` / `bho_` arrays.
 */
const APPLICABILITY_RULES: Readonly<
  Record<string, Readonly<Record<string, readonly ApplicabilityRule[]>>>
> = {
  unifiedmedbv: MEDBV_ENTITIES,
  medbv: MEDBV_ENTITIES,
  bho: MEDBV_ENTITIES,
};

function matches(rule: ApplicabilityRule, path: string): boolean {
  if (rule.except?.includes(path)) return false;
  return rule.fields.some(pattern =>
    pattern.endsWith('.*') ? path.startsWith(pattern.slice(0, -1)) : pattern === path,
  );
}

/**
 * Why `relativePath` does not apply to one entity element, or undefined when it does
 * (or when nothing says otherwise).
 *
 * @param element the entity element's current output value (`drug[i]`), gates read from it
 */
export function notApplicableReason(
  workflowType: string | undefined,
  entityKey: string,
  relativePath: string,
  element: unknown,
): string | undefined {
  const rules = workflowType ? APPLICABILITY_RULES[workflowType]?.[entityKey] : undefined;
  if (!rules) return undefined;
  for (const rule of rules) {
    if (!matches(rule, relativePath)) continue;
    const answer = answerOf(readRelativePath(element, rule.gate));
    if (answer !== 'unknown' && answer !== rule.expect) return rule.reason;
  }
  return undefined;
}
